/**
 * DocumentPreviewDialog — canonical in-app documentviewer.
 *
 * Root cause fix (Jun 2026): `ConsolidationDocumentsTab` en
 * `ShipmentDocumentsTab` openden documenten via
 * `window.open(URL.createObjectURL(blob))`. Chrome blokkeert top-level
 * navigatie naar een `blob:`-URL die ná een async fetch wordt geopend →
 * er opende een LEEG tabblad (URL `:`). Dat is exact het gemelde
 * "Bekijken leidt naar een lege link / Download levert een leeg bestand op".
 *
 * Deze component gebruikt hetzelfde patroon als de reeds werkende
 * `ConsolidationLabelButton` uit Sprint 5.3: fetch als blob → render in een
 * `<iframe>` binnen een dialog. Geen popups, geen blob-navigatie.
 *
 * Extra garanties:
 *   • 0-byte responses worden als fout behandeld, niet als leeg document.
 *   • Een PDF-response die geen `%PDF` header heeft wordt geweigerd.
 *   • Printen gebeurt via het iframe zelf (geen tweede fetch).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Printer, RefreshCw, X } from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";

const TEXT_PREVIEWABLE = ["text/", "application/json", "application/xml"];

function filenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback;
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export async function unpackDocumentResponse(response) {
  if (!response.headers?.["content-type"]?.startsWith("multipart/form-data")) {
    return { blob: response.data, report: null };
  }
  // Native multipart parsing keeps the binary and the full report from ONE export.
  const parts = await new Response(response.data, {
    headers: { "Content-Type": response.headers["content-type"] },
  }).formData();
  const blob = parts.get("document");
  const report = JSON.parse(parts.get("report"));
  if (!(blob instanceof Blob) || !blob.size || !Array.isArray(report?.skipped)
      || !["complete", "incomplete"].includes(report.status)) {
    throw new Error("Download of dossieroverzicht is ongeldig");
  }
  return { blob, report };
}

export async function documentResponseError(error) {
  if (error?.response?.status >= 500) return "Document kon niet worden gegenereerd. Probeer opnieuw.";
  if (error?.response?.data instanceof Blob) {
    try {
      const data = JSON.parse(await error.response.data.text());
      return formatApiError({ ...error, response: { ...error.response, data } });
    } catch { /* A non-JSON error still gets the normal safe message. */ }
  }
  return formatApiError(error) || error?.message || "Download mislukt";
}

export const DossierNotice = ({ report, testId = "dossier-export-notice" }) => {
  if (!report || (report.status !== "incomplete" && !report.skipped?.length)) return null;
  const incomplete = report.status === "incomplete";
  return <div role={incomplete ? "alert" : "status"} data-testid={testId}
    className={`shrink-0 px-4 py-3 text-xs border-b min-w-0 break-words ${incomplete ? "bg-amber-50 text-amber-950 border-amber-300" : "bg-slate-50 text-slate-700 border-slate-200"}`}>
    <p className="font-bold" data-testid={`${testId}-status`}>
      {incomplete ? "Onvolledig" : "PDF — niet-printbare bijlagen"} · {report.included_count} documenten opgenomen
    </p>
    {!report.included_count && <p data-testid={`${testId}-empty`}>Geen brondocumenten opgenomen; alleen het dossieroverzicht is beschikbaar.</p>}
    <ul className="mt-1 max-h-32 overflow-auto space-y-1 [overflow-wrap:anywhere]" data-testid={`${testId}-list`}>
      {report.skipped.map((item, index) => <li key={`${item.id}-${index}`} data-testid={`${testId}-item-${index}`}>
        {item.folder}/{item.filename || item.label} [{item.id}] — {item.reason}
      </li>)}
    </ul>
  </div>;
};

export default function DocumentPreviewDialog({
  open,
  onClose,
  title,
  subtitle,
  url,
  downloadUrl,
  fallbackFilename = "document.pdf",
  autoPrint = false,
  embedded = false,
  allowDownload = true,
  requestClient = adminApi,
  refreshBeforePrint = false,
  onDossierReport,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [mime, setMime] = useState(null);
  const [textPreview, setTextPreview] = useState(null);
  const [resolvedName, setResolvedName] = useState(fallbackFilename);
  const [downloading, setDownloading] = useState(false);
  const [dossier, setDossier] = useState(null);
  const pendingPrintRef = useRef(false);
  const iframeRef = useRef(null);
  const blobUrlRef = useRef(null);
  const generationRef = useRef(0);
  const printTimerRef = useRef(null);
  // autoPrint mag per keer openen exact ÉÉN printdialoog triggeren — ook als
  // React de component opnieuw rendert of het iframe een tweede load-event geeft.
  const printedRef = useRef(false);

  const cleanup = useCallback(() => {
    generationRef.current += 1;
    if (printTimerRef.current !== null) {
      window.clearTimeout(printTimerRef.current);
      printTimerRef.current = null;
    }
    if (blobUrlRef.current) {
      // The native PDF reader can still be releasing a frame/print operation.
      // Detach stale content immediately, but do not invalidate its bytes mid-print.
      const retiredUrl = blobUrlRef.current;
      window.setTimeout(() => window.URL.revokeObjectURL(retiredUrl), 60000);
      blobUrlRef.current = null;
    }
  }, []);

  const load = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    setError(null);
    setDossier(null);
    setTextPreview(null);
    cleanup();
    const generation = generationRef.current;
    setBlobUrl(null);
    try {
      const resp = await requestClient.get(url, { responseType: "blob", ...(refreshBeforePrint ? { headers: { "Cache-Control": "no-cache" } } : {}) });
      const { blob, report } = await unpackDocumentResponse(resp);
      if (generation !== generationRef.current) return;
      if (resp.headers?.["x-dossier-version"]) setDossier(report || { version: resp.headers["x-dossier-version"],
        generated_at: resp.headers["x-dossier-generated-at"], document_count: resp.headers["x-dossier-document-count"] });
      if (report) onDossierReport?.(report);
      if (!blob || blob.size === 0) {
        throw new Error("Document is leeg (0 bytes) — bron is niet beschikbaar");
      }
      const contentType = blob.type || resp.headers?.["content-type"] || "";
      const name = filenameFromDisposition(
        resp.headers?.["content-disposition"], fallbackFilename,
      );
      setResolvedName(name);
      setMime(contentType);

      if (contentType.includes("pdf")) {
        const head = await blob.slice(0, 5).text();
        if (!head.startsWith("%PDF")) {
          throw new Error("Ongeldige PDF ontvangen — bron levert geen document");
        }
      }
      if (TEXT_PREVIEWABLE.some((t) => contentType.startsWith(t))) {
        const txt = await blob.text();
        setTextPreview(txt.slice(0, 200000));
      }
      if (generation !== generationRef.current) return;
      const objUrl = window.URL.createObjectURL(blob);
      blobUrlRef.current = objUrl;
      setBlobUrl(objUrl);
    } catch (e) {
      if (generation !== generationRef.current) return;
      pendingPrintRef.current = false;
      setError(e?.response?.status === 404
        ? "Document niet gevonden of bestand ontbreekt"
        : e?.response?.status === 403
          ? "Geen toegang tot dit document"
          : await documentResponseError(e));
    } finally {
      if (generation === generationRef.current) setLoading(false);
    }
  }, [url, fallbackFilename, cleanup, refreshBeforePrint, requestClient, onDossierReport]);

  useEffect(() => {
    if (open) load();
    return () => { cleanup(); pendingPrintRef.current = false; };
  }, [open, load, cleanup]);

  useEffect(() => {
    printedRef.current = false;
  }, [open, url, autoPrint]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const resp = await requestClient.get(downloadUrl || url, { responseType: "blob" });
      const { blob, report } = await unpackDocumentResponse(resp);
      if (!blob || blob.size === 0) {
        throw new Error("Download is leeg (0 bytes)");
      }
      if (report) { setDossier(report); onDossierReport?.(report); }
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filenameFromDisposition(
        resp.headers?.["content-disposition"], resolvedName,
      );
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.setTimeout(() => window.URL.revokeObjectURL(objUrl), 60000);
      if (report?.status === "incomplete") toast.warning("Onvolledig — beschikbare documenten gedownload", {
        description: "Niet-verwerkte documenten staan in de melding en het dossieroverzicht.",
      });
      else toast.success("Download gestart");
    } catch (e) {
      toast.error(await documentResponseError(e));
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = useCallback((expectedFrame = iframeRef.current) => {
    const frame = expectedFrame;
    if (!frame) {
      toast.error("Printen niet beschikbaar — gebruik Downloaden");
      return;
    }
    if (frame !== iframeRef.current || !frame.isConnected ||
        frame.getAttribute("src")?.split("#")[0] !== blobUrlRef.current) return;
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch {
      toast.error("Printen geblokkeerd door de browser — gebruik Downloaden");
    }
  }, []);

  // Printen-actie: preview openen, wachten tot het iframe de PDF écht heeft
  // geladen en dan de systeem-printdialoog openen. Nooit window.open(blob).
  const handleFrameLoad = useCallback((event) => {
    const frame = event.currentTarget;
    if (frame !== iframeRef.current || !frame.isConnected ||
        frame.getAttribute("src")?.split("#")[0] !== blobUrlRef.current) return;
    if (!pendingPrintRef.current && (!autoPrint || printedRef.current)) return;
    pendingPrintRef.current = false;
    printedRef.current = true;
    if (printTimerRef.current !== null) window.clearTimeout(printTimerRef.current);
    // Bind this pending action to exactly the loaded frame, never its successor.
    printTimerRef.current = window.setTimeout(() => {
      printTimerRef.current = null;
      handlePrint(frame);
    }, 400);
  }, [autoPrint, handlePrint]);

  if (!open) return null;

  const isPdf = (mime || "").includes("pdf");

  return (
    <div
      className={embedded ? "w-full min-w-0 border border-gray-200" : "fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"}
      data-testid="document-preview-dialog"
    >
      <div className={`bg-white w-full flex flex-col ${embedded ? "h-[60vh]" : "max-w-5xl h-[88vh] shadow-2xl"}`}>
        <header className="flex flex-wrap items-start justify-between gap-4 px-4 py-3 bg-[#082c50] text-white">
          <div className="min-w-0 max-w-full">
            <h2 className="font-chivo font-bold text-base break-words"
                data-testid="document-preview-title">
              {title || "Document"}
            </h2>
            {subtitle && (
              <p className="text-[11px] text-blue-100 break-words mt-0.5" data-testid="document-preview-subtitle">{subtitle}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
            {isPdf && blobUrl && (
              <Button
                size="sm" variant="outline"
                onClick={() => {
                  if (refreshBeforePrint) { pendingPrintRef.current = true; load(); }
                  else handlePrint();
                }}
                className="h-8 rounded-none text-[10px] uppercase font-bold bg-white/10 border-white/30 text-white hover:bg-white/20"
                data-testid="document-preview-print"
              >
                <Printer className="w-3.5 h-3.5 mr-1" /> Printen
              </Button>
            )}
            {allowDownload && <Button
              size="sm"
              onClick={handleDownload}
              disabled={downloading || loading}
              className="h-8 rounded-none text-[10px] uppercase font-bold bg-[#0055FF] hover:bg-[#003fcc] text-white"
              data-testid="document-preview-download"
            >
              {downloading
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Download className="w-3.5 h-3.5 mr-1" />}
              Download
            </Button>}
            <button
              type="button" onClick={onClose}
              className="p-1.5 hover:bg-white/20 transition-colors"
              title="Sluiten" data-testid="document-preview-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>
        {dossier && <div className="px-4 py-2 border-b text-xs break-words" data-testid="dossier-version-summary">
          <span data-testid="dossier-version">Versie {dossier.version.slice(0, 20)}</span>{" · "}
          <span data-testid="dossier-document-count">{dossier.document_count} documenten</span>{" · "}
          <span data-testid="dossier-generation-time">{dossier.generated_at}</span>
        </div>}
        <DossierNotice report={dossier} testId="dossier-preview-notice" />

        <div className="flex-1 min-h-0 bg-slate-100">
          {loading && (
            <div className="h-full flex flex-col items-center justify-center text-slate-500"
                 data-testid="document-preview-loading">
              <Loader2 className="w-6 h-6 animate-spin mb-2" />
              <p className="text-sm">Document laden…</p>
            </div>
          )}
          {!loading && error && (
            <div className="h-full flex flex-col items-center justify-center px-6 text-center"
                 data-testid="document-preview-error">
              <p className="text-sm font-medium text-red-700">
                Document kon niet worden geopend
              </p>
              <p className="text-xs text-slate-600 mt-1 max-w-lg">{error}</p>
              <Button
                size="sm" variant="outline" onClick={load}
                className="mt-4 rounded-none text-[10px] uppercase font-bold"
                data-testid="document-preview-retry"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Opnieuw proberen
              </Button>
            </div>
          )}
          {!loading && !error && blobUrl && (
            textPreview !== null ? (
              <pre className="h-full overflow-auto p-4 text-[11px] font-mono whitespace-pre-wrap bg-white"
                   data-testid="document-preview-text">{textPreview}</pre>
            ) : /^(image\/(png|jpeg|webp|gif))$/.test(mime.split(";")[0]) ? (
              <div className="h-full w-full overflow-auto bg-white p-3 flex items-center justify-center"><img src={blobUrl} alt={title || resolvedName} className="max-w-full max-h-full object-contain" data-testid="document-preview-image" /></div>
            ) : isPdf ? (
              <iframe
                ref={iframeRef}
                src={dossier ? `${blobUrl}#view=FitH` : blobUrl}
                title={title || "Document"}
                onLoad={handleFrameLoad}
                className="w-full h-full border-0"
                data-testid="document-preview-frame"
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center px-6 text-center"
                   data-testid="document-preview-unsupported">
                <p className="text-sm font-medium text-[#082c50]">
                  Dit bestandstype kan niet in de browser worden weergegeven
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  {resolvedName} · {mime || "onbekend type"}
                </p>
                {allowDownload && <Button
                  size="sm" onClick={handleDownload} disabled={downloading}
                  className="mt-4 rounded-none text-[10px] uppercase font-bold bg-[#0055FF] text-white"
                  data-testid="document-preview-download-alt"
                >
                  <Download className="w-3.5 h-3.5 mr-1" /> Downloaden
                </Button>}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
